import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Subject, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from 'projects/shared/src/lib/auth/auth/data-access/auth.service';
import { ConfigurationService } from '@shared-utils/configuration/configuration.service';
import { LoginConfig } from '@shared-interfaces/shared-login';

export type LoginStatus = 'pending' | 'authenticating' | 'success' | 'error';

interface LoginState {
  status: LoginStatus;
}

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private authService = inject(AuthService);
  private configService = inject(ConfigurationService);
  // sources
  error$ = new Subject<any>();
  login$ = new Subject<string>();

  userAuthenticated$ = this.login$.pipe(
    switchMap((selectedProvider) =>
      this.authService.login(selectedProvider).pipe(
        catchError((err) => {
          console.error(err);
          this.error$.next(err);
          return EMPTY;
        }),
      ),
    ),
  );

  // state
  private state = signal<LoginState>({
    status: 'pending',
  });

  // selectors
  status = computed(() => this.state().status);
  loginConfig = signal<LoginConfig | undefined>(undefined);

  constructor() {
    // reducers
    this.userAuthenticated$
      .pipe(takeUntilDestroyed())
      .subscribe(() =>
        this.state.update((state) => ({ ...state, status: 'success' })),
      );

    this.login$
      .pipe(takeUntilDestroyed())
      .subscribe(() =>
        this.state.update((state) => ({ ...state, status: 'authenticating' })),
      );

    this.error$
      .pipe(takeUntilDestroyed())
      .subscribe(() =>
        this.state.update((state) => ({ ...state, status: 'error' })),
      );
  }

  getLoginConfig(): void {
    this.configService.getConfigItem('/assets/data/login.json').subscribe({
      next: (loginConfig: LoginConfig) => {
        this.loginConfig.set(loginConfig);
      },
      error: (error) => {
        console.error(error);
      },
    });
  }
}
